// src/pages/CaseStudyDetail.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import MediaDisplay from '../components/MediaDisplay';
import RichText from '../components/RichText';
import PresentationDisplay from '../components/PresentationDisplay';
import CaseStudyLightbox from '../components/CaseStudyLightbox';
import { tagCategoryNames, tagCategoryColors } from '../utils/tagConfig';
import caseStudyDetailsData from '../data/caseStudyDetails.json';
import { imageMap } from '../utils/imageImports';
import MediaGrid from '../components/MediaGrid';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Page wrapper
const DetailWrapper = styled.div`
  /* A readable measure: body text should wrap near 70-75 characters, and figures sit in a
     centered frame instead of stretching to the viewport. */
  max-width: 800px;
  margin: 0 auto;
  padding: calc(70px + ${({ theme }) => theme.spacing['2xl']}) 20px ${({ theme }) => theme.spacing['2xl']};
  animation: ${fadeIn} 0.6s ${({ theme }) => theme.transitions.easeOut};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: calc(60px + ${({ theme }) => theme.spacing.xl}) 20px ${({ theme }) => theme.spacing.xl};
  }
`;

// Back button - Secondary variant (outlined)
const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.base};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  background: transparent;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary}15;
  }

  &:focus {
    outline: 3px solid ${({ theme }) => theme.colors.primary}40;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Title section
const TitleSection = styled.header`
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  animation: ${slideIn} 0.6s ${({ theme }) => theme.transitions.easeOut};
`;

const CompanyName = styled.div`
  color: ${({ theme }) => theme.colors.textTertiary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1.2;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.5;
`;

// Metadata section
const MetaSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 0;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  
  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.primary};
    opacity: 0.7;
  }
  
  span {
    color: ${({ theme }) => theme.colors.textTertiary};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    margin-right: ${({ theme }) => theme.spacing.xs};
  }
  
  strong {
    color: ${({ theme }) => theme.colors.text};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }
`;

// Hero image
const HeroImage = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  border-radius: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

// Content section
const ContentSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  
  > * + * {
    margin-top: ${({ theme }) => theme.spacing.lg};
  }
`;

// Tags section
const TagsSection = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const TagCategory = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const TagTitle = styled.h3`
  color: ${({ $color, theme }) => $color || theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Tag = styled.span`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ $color }) => $color ? `${$color}10` : 'rgba(135, 182, 194, 0.1)'};
  color: ${({ $color, theme }) => $color || theme.colors.text};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ $color }) => $color ? `${$color}20` : 'rgba(135, 182, 194, 0.2)'};
  }
`;

const CaseStudyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseStudy, setCaseStudy] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    const studyId = parseInt(id);
    const foundStudy = caseStudyDetailsData.caseStudyDetails.find(cs => cs.id === studyId);

    if (foundStudy) {
      setCaseStudy(foundStudy);
    } else {
      navigate('/');
    }
  }, [id, navigate]);

  // Collect all zoomable image URLs into a flat array
  const allMedia = useMemo(() => {
    if (!caseStudy) return [];
    const media = [];

    // Thumbnail
    if (caseStudy.thumbnail) {
      media.push(imageMap[caseStudy.thumbnail] || caseStudy.thumbnail);
    }

    // Content sections
    if (caseStudy.content) {
      caseStudy.content.forEach((section) => {
        if (section.type === 'image' || section.type === 'image_grid' || section.type === 'image_sidebyside' || section.type === 'gif') {
          const sources = section.imagesources || section.src || section.content || '';
          const imageArray = sources.split(';').map(s => s.trim()).filter(Boolean);
          imageArray.forEach((img) => {
            media.push(imageMap[img] || img);
          });
        }
      });
    }

    // Legacy media gallery
    if (caseStudy.media) {
      caseStudy.media.forEach((m) => {
        if (m.type !== 'video') {
          const src = m.url || m.src;
          if (src) media.push(imageMap[src] || src);
        }
      });
    }

    return media;
  }, [caseStudy]);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(-1);
  }, []);

  if (!caseStudy) {
    return null;
  }

  // Helper function to format modality array
  const formatModality = (modality) => {
    if (Array.isArray(modality)) {
      return modality.join(', ');
    }
    return modality || 'Web / mobile';
  };

  // Get thumbnail image
  const thumbnailSrc = imageMap[caseStudy.thumbnail] || caseStudy.thumbnail;

  return (
    <DetailWrapper>
      <BackButton onClick={() => navigate('/')}>
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        All Projects
      </BackButton>

      <TitleSection>
        {caseStudy.company && <CompanyName>{caseStudy.company}</CompanyName>}
        <Title>{caseStudy.title}</Title>
        {caseStudy.subtitle && <Subtitle>{caseStudy.subtitle}</Subtitle>}
      </TitleSection>

      {/* Metadata */}
      <MetaSection>
        {caseStudy.duration && (
          <MetaItem>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Duration</span>
            <strong>{caseStudy.duration}</strong>
          </MetaItem>
        )}
        
        {caseStudy.role && (
          <MetaItem>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span>Role</span>
            <strong>{caseStudy.role}</strong>
          </MetaItem>
        )}
        
        {caseStudy.interactionModality && (
          <MetaItem>
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
              <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
            </svg>
            <span>Platform</span>
            <strong>{formatModality(caseStudy.interactionModality)}</strong>
          </MetaItem>
        )}
      </MetaSection>

      {/* Hero image/thumbnail */}
      {caseStudy.thumbnail && (
        <HeroImage onClick={() => openLightbox(0)} style={{ cursor: 'zoom-in' }}>
          <img src={thumbnailSrc} alt={caseStudy.title} />
        </HeroImage>
      )}

      {/* Content sections with inline media and presentations */}
      <ContentSection>
        {(() => {
          // Track running global media index (thumbnail is index 0)
          let mediaOffset = caseStudy.thumbnail ? 1 : 0;

          return caseStudy.content && caseStudy.content.map((section, index) => {
            // Handle presentation content type
            if (section.type === 'presentation') {
              return (
                <PresentationDisplay
                  key={section.key || index}
                  type={section.presentation_type}
                  slides={section.slides}
                  pdfSrc={section.pdf_src}
                  googleSlidesUrl={section.google_slides_url}
                  embedUrl={section.embed_url}
                  embedHeight={section.embed_height}
                  title={section.title}
                  caption={section.caption}
                  allowDownload={section.allow_download}
                  showThumbnails={section.show_thumbnails}
                  autoPlay={section.auto_play}
                  autoPlayInterval={section.autoplay_interval}
                />
              );
            }

            // Handle image layouts (image, image_grid, image_sidebyside)
            if (section.type === 'image' || section.type === 'image_grid' || section.type === 'image_sidebyside') {
              const sources = section.imagesources || section.src || section.content || '';
              let layout = section.layout || 'fullWidth';

              if (section.type === 'image_grid' && !layout.includes('grid') && !layout.includes('sidebyside')) {
                layout = 'grid-2';
              }
              if (section.type === 'image_sidebyside' && !layout.includes('sidebyside') && !layout.includes('grid')) {
                layout = 'sidebyside-equal';
              }

              const hasMultipleImages = sources.includes(';');
              const imageArray = sources.split(';').map(s => s.trim()).filter(Boolean);
              const currentOffset = mediaOffset;
              mediaOffset += imageArray.length;

              // Multi-image layouts
              if ((layout.includes('grid') || layout.includes('sidebyside')) ||
                  (hasMultipleImages && section.type !== 'image')) {
                const resolvedImages = imageArray.map(img => imageMap[img] || img);
                return (
                  <MediaGrid
                    key={section.key || index}
                    images={resolvedImages}
                    layout={layout}
                    alts={section.alt}
                    captions={section.caption}
                    onImageClick={openLightbox}
                    globalIndexOffset={currentOffset}
                  />
                );
              }

              // Single image
              const singleImageSrc = imageArray[0];
              const resolvedSingleImage = imageMap[singleImageSrc] || singleImageSrc;
              return (
                <MediaDisplay
                  key={section.key || index}
                  url={resolvedSingleImage}
                  alt={section.alt?.split(';')[0]?.trim()}
                  caption={section.caption?.split(';')[0]?.trim()}
                  fullWidth={layout === 'fullWidth'}
                  type="image"
                  size={section.size}
                  onImageClick={() => openLightbox(currentOffset)}
                />
              );
            }

            // Handle video/media content types (no lightbox for videos)
            if (section.type === 'media' || section.type === 'video') {
              return (
                <MediaDisplay
                  key={section.key || index}
                  url={section.src || section.content}
                  alt={section.alt}
                  type={section.type === 'video' ? 'video' : section.mediaType}
                  caption={section.caption}
                  poster={section.poster}
                  autoplay={section.autoplay}
                  loop={section.loop}
                  muted={section.muted}
                  fullWidth={section.fullWidth}
                />
              );
            }

            // Handle GIF content type
            if (section.type === 'gif') {
              const currentOffset = mediaOffset;
              mediaOffset += 1;
              return (
                <MediaDisplay
                  key={section.key || index}
                  url={section.src || section.content}
                  alt={section.alt}
                  type="image"
                  caption={section.caption}
                  fullWidth={section.layout === 'fullWidth'}
                  onImageClick={() => openLightbox(currentOffset)}
                />
              );
            }

            // Handle text content types
            return (
              <RichText
                key={section.key || index}
                content={section.content}
                type={section.type}
              />
            );
          });
        })()}
      </ContentSection>

      {/* Legacy media gallery (optional, for backwards compatibility) */}
      {caseStudy.media && caseStudy.media.length > 0 && (
        <ContentSection>
          <h3 style={{ 
            marginBottom: '1.5rem',
            color: 'var(--text-secondary)',
            fontSize: '1.25rem',
            fontWeight: 600
          }}>
            Additional Media
          </h3>
          {caseStudy.media.map((media, index) => (
            <MediaDisplay
              key={index}
              url={media.url || media.src}
              alt={media.alt}
              type={media.type}
              caption={media.caption}
            />
          ))}
        </ContentSection>
      )}

      {/* Tags (visually hidden, kept for accessibility/search) */}
      {caseStudy.tags && (
        <TagsSection aria-hidden="false">
          {Object.entries(caseStudy.tags).map(([category, tags]) => {
            if (!tags || tags.length === 0) return null;
            return (
              <TagCategory key={category}>
                <TagTitle $color={tagCategoryColors[category]}>
                  {tagCategoryNames[category]}
                </TagTitle>
                <TagList>
                  {tags.map((tag, index) => (
                    <Tag key={index} $color={tagCategoryColors[category]}>
                      {tag}
                    </Tag>
                  ))}
                </TagList>
              </TagCategory>
            );
          })}
        </TagsSection>
      )}

      {/* Gallery lightbox with prev/next navigation */}
      {lightboxIndex >= 0 && allMedia.length > 0 && (
        <CaseStudyLightbox
          images={allMedia}
          activeIndex={lightboxIndex}
          onClose={closeLightbox}
          onNavigate={setLightboxIndex}
        />
      )}
    </DetailWrapper>
  );
};

export default CaseStudyDetail;